import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, Info, Award, TrendingUp, Users, MapPin, BookOpen, Briefcase, Target } from 'lucide-react';

interface PointsBreakdown {
  businessType: { points: number; max: number; description: string };
  education: { points: number; max: number; description: string };
  courses: { points: number; max: number; description: string };
  experience: { points: number; max: number; description: string };
  location: { points: number; max: number; description: string };
  preferenceGroups: { points: number; max: number; description: string };
  total: number;
  requiredMinimum: number;
  qualifies: boolean;
}

interface Issue {
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  category: string;
  description: string;
  recommendation: string;
}

interface ComprehensiveAnalysis {
  pointsBreakdown: PointsBreakdown;
  criticalIssues: Issue[];
  warnings: Issue[];
  suggestions: Issue[];
  strengths: string[];
  weaknesses: string[];
  marketAnalysis: string;
  competitivenessAssessment: string;
  overallRecommendation: string;
  missingDocuments: string[];
  requiredDocuments: string[];
  finalVerdict: 'RECOMMENDED' | 'CONDITIONAL' | 'NOT_RECOMMENDED' | 'DISQUALIFIED';
  summaryText: string;
}

interface DynamicBreakdownItem {
  ruleId: string;
  label: string;
  points: number;
  maxPoints: number;
  description: string;
  icon?: string;
}

interface Props {
  analysis: ComprehensiveAnalysis;
  onClose?: () => void;
  dynamicBreakdown?: DynamicBreakdownItem[];
  dynamicMinPoints?: number;
}

function getIconByName(name?: string): React.ReactElement {
  switch (name) {
    case 'Briefcase': return <Briefcase className="w-5 h-5" />;
    case 'BookOpen': return <BookOpen className="w-5 h-5" />;
    case 'Award': return <Award className="w-5 h-5" />;
    case 'TrendingUp': return <TrendingUp className="w-5 h-5" />;
    case 'MapPin': return <MapPin className="w-5 h-5" />;
    case 'Users': return <Users className="w-5 h-5" />;
    case 'Target': return <Target className="w-5 h-5" />;
    default: return <Award className="w-5 h-5" />;
  }
}

export function ComprehensiveAnalysisDisplay({ analysis, onClose, dynamicBreakdown, dynamicMinPoints }: Props) {
  const getVerdictColor = (verdict: string) => {
    switch (verdict) {
      case 'RECOMMENDED': return 'bg-green-100 border-green-500 text-green-900';
      case 'CONDITIONAL': return 'bg-yellow-100 border-yellow-500 text-yellow-900';
      case 'NOT_RECOMMENDED': return 'bg-orange-100 border-orange-500 text-orange-900';
      case 'DISQUALIFIED': return 'bg-red-100 border-red-500 text-red-900';
      default: return 'bg-gray-100 border-gray-500 text-gray-900';
    }
  };

  const getVerdictIcon = (verdict: string) => {
    switch (verdict) {
      case 'RECOMMENDED': return <CheckCircle className="w-8 h-8" />;
      case 'CONDITIONAL': return <AlertTriangle className="w-8 h-8" />;
      case 'NOT_RECOMMENDED': return <XCircle className="w-8 h-8" />;
      case 'DISQUALIFIED': return <XCircle className="w-8 h-8" />;
      default: return <Info className="w-8 h-8" />;
    }
  };

  const getVerdictLabel = (verdict: string) => {
    switch (verdict) {
      case 'RECOMMENDED': return '✅ REKOMENDOWANY';
      case 'CONDITIONAL': return '⚠️ WARUNKOWO';
      case 'NOT_RECOMMENDED': return '❌ NIE REKOMENDOWANY';
      case 'DISQUALIFIED': return '🚫 ZDYSKWALIFIKOWANY';
      default: return 'NIEZNANY';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-xl max-w-7xl mx-auto p-6 space-y-6">
      {/* Header with Close Button */}
      {onClose && (
        <div className="flex justify-between items-center border-b pb-4">
          <h1 className="text-3xl font-bold text-gray-800">📊 Szczegółowa Analiza AI</h1>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-3xl font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Final Verdict Card */}
      <div className={`border-4 rounded-xl p-6 ${getVerdictColor(analysis.finalVerdict)}`}>
        <div className="flex items-center gap-4">
          {getVerdictIcon(analysis.finalVerdict)}
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2">{getVerdictLabel(analysis.finalVerdict)}</h2>
            <p className="text-lg">{analysis.summaryText}</p>
          </div>
        </div>
      </div>

      {/* Points Breakdown */}
      {dynamicBreakdown ? (() => {
        const dynTotal = dynamicBreakdown.reduce((sum, item) => sum + item.points, 0);
        const dynMin = dynamicMinPoints ?? analysis.pointsBreakdown.requiredMinimum;
        const dynQualifies = dynTotal >= dynMin;

        return (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-300">
            <h3 className="text-2xl font-bold text-blue-900 mb-4 flex items-center gap-2">
              <Award className="w-6 h-6" />
              Punktacja
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Total Score */}
              <div className="col-span-full bg-white rounded-lg p-6 shadow-md">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-600 text-sm">SUMA PUNKTÓW</p>
                    <p className={`text-5xl font-bold ${dynQualifies ? 'text-green-600' : 'text-red-600'}`}>
                      {dynTotal} / {dynMin}
                    </p>
                  </div>
                  <div className={`text-6xl ${dynQualifies ? 'text-green-500' : 'text-red-500'}`}>
                    {dynQualifies ? '✓' : '✗'}
                  </div>
                </div>
                <div className="mt-4 bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full transition-all ${dynQualifies ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min((dynTotal / dynMin) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {dynQualifies
                    ? 'Kandydat spełnia minimum punktowe!'
                    : `Brakuje ${dynMin - dynTotal} pkt do kwalifikacji`}
                </p>
              </div>

              {/* Dynamic Categories */}
              {dynamicBreakdown.map((item) => (
                <PointsCard
                  key={item.ruleId}
                  icon={getIconByName(item.icon)}
                  title={item.label}
                  points={item.points}
                  max={item.maxPoints}
                  description={item.description}
                />
              ))}
            </div>
          </div>
        );
      })() : (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-300">
        <h3 className="text-2xl font-bold text-blue-900 mb-4 flex items-center gap-2">
          <Award className="w-6 h-6" />
          Punktacja PUP Kraków 2026
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Total Score */}
          <div className="col-span-full bg-white rounded-lg p-6 shadow-md">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600 text-sm">SUMA PUNKTÓW</p>
                <p className={`text-5xl font-bold ${analysis.pointsBreakdown.qualifies ? 'text-green-600' : 'text-red-600'}`}>
                  {analysis.pointsBreakdown.total} / {analysis.pointsBreakdown.requiredMinimum}
                </p>
              </div>
              <div className={`text-6xl ${analysis.pointsBreakdown.qualifies ? 'text-green-500' : 'text-red-500'}`}>
                {analysis.pointsBreakdown.qualifies ? '✓' : '✗'}
              </div>
            </div>
            <div className="mt-4 bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full transition-all ${analysis.pointsBreakdown.qualifies ? 'bg-green-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min((analysis.pointsBreakdown.total / 23) * 100, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {analysis.pointsBreakdown.qualifies
                ? 'Kandydat spełnia minimum punktowe!'
                : `Brakuje ${23 - analysis.pointsBreakdown.total} pkt do kwalifikacji`}
            </p>
          </div>

          {/* Individual Categories */}
          <PointsCard
            icon={<Briefcase className="w-5 h-5" />}
            title="Rodzaj działalności"
            points={analysis.pointsBreakdown.businessType.points}
            max={analysis.pointsBreakdown.businessType.max}
            description={analysis.pointsBreakdown.businessType.description}
          />

          <PointsCard
            icon={<BookOpen className="w-5 h-5" />}
            title="Wykształcenie"
            points={analysis.pointsBreakdown.education.points}
            max={analysis.pointsBreakdown.education.max}
            description={analysis.pointsBreakdown.education.description}
          />

          <PointsCard
            icon={<Award className="w-5 h-5" />}
            title="Kursy z certyfikatami"
            points={analysis.pointsBreakdown.courses.points}
            max={analysis.pointsBreakdown.courses.max}
            description={analysis.pointsBreakdown.courses.description}
          />

          <PointsCard
            icon={<TrendingUp className="w-5 h-5" />}
            title="Doświadczenie zawodowe"
            points={analysis.pointsBreakdown.experience.points}
            max={analysis.pointsBreakdown.experience.max}
            description={analysis.pointsBreakdown.experience.description}
          />

          <PointsCard
            icon={<MapPin className="w-5 h-5" />}
            title="Lokalizacja"
            points={analysis.pointsBreakdown.location.points}
            max={analysis.pointsBreakdown.location.max}
            description={analysis.pointsBreakdown.location.description}
          />

          <PointsCard
            icon={<Users className="w-5 h-5" />}
            title="Grupy preferencyjne"
            points={analysis.pointsBreakdown.preferenceGroups.points}
            max={analysis.pointsBreakdown.preferenceGroups.max}
            description={analysis.pointsBreakdown.preferenceGroups.description}
          />
        </div>
      </div>
      )}

      {/* Critical Issues */}
      {analysis.criticalIssues.length > 0 && (
        <div className="bg-red-50 border-2 border-red-400 rounded-xl p-6">
          <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
            <XCircle className="w-6 h-6" />
            🚫 Błędy Dyskwalifikujące ({analysis.criticalIssues.length})
          </h3>
          <div className="space-y-3">
            {analysis.criticalIssues.map((issue, idx) => (
              <IssueCard key={idx} issue={issue} />
            ))}
          </div>
        </div>
      )}

      {/* Warnings */}
      {analysis.warnings.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6">
          <h3 className="text-xl font-bold text-yellow-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" />
            ⚠️ Ostrzeżenia ({analysis.warnings.length})
          </h3>
          <div className="space-y-3">
            {analysis.warnings.map((issue, idx) => (
              <IssueCard key={idx} issue={issue} />
            ))}
          </div>
        </div>
      )}

      {/* AI Analysis */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Strengths */}
        {analysis.strengths.length > 0 && (
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6">
            <h3 className="text-lg font-bold text-green-900 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5" />
              💪 Mocne strony
            </h3>
            <ul className="space-y-2">
              {analysis.strengths.map((strength, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-green-800">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Weaknesses */}
        {analysis.weaknesses.length > 0 && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-6">
            <h3 className="text-lg font-bold text-orange-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              ⚠️ Do poprawy
            </h3>
            <ul className="space-y-2">
              {analysis.weaknesses.map((weakness, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-orange-800">
                  <span className="text-orange-600 font-bold mt-0.5">!</span>
                  <span>{weakness}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Market Analysis */}
      {analysis.marketAnalysis && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
          <h3 className="text-lg font-bold text-blue-900 mb-3">📈 Analiza rynku</h3>
          <p className="text-sm text-blue-800">{analysis.marketAnalysis}</p>
        </div>
      )}

      {/* Competitiveness */}
      {analysis.competitivenessAssessment && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-6">
          <h3 className="text-lg font-bold text-purple-900 mb-3">🎯 Ocena konkurencyjności</h3>
          <p className="text-sm text-purple-800">{analysis.competitivenessAssessment}</p>
        </div>
      )}

      {/* Overall Recommendation */}
      {analysis.overallRecommendation && (
        <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-6">
          <h3 className="text-lg font-bold text-indigo-900 mb-3">💡 Rekomendacja ogólna</h3>
          <p className="text-sm text-indigo-800">{analysis.overallRecommendation}</p>
        </div>
      )}

      {/* Suggestions */}
      {analysis.suggestions.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
          <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
            <Info className="w-6 h-6" />
            💡 Sugestie poprawy punktacji
          </h3>
          <div className="space-y-3">
            {analysis.suggestions.map((issue, idx) => (
              <IssueCard key={idx} issue={issue} />
            ))}
          </div>
        </div>
      )}

      {/* Required Documents */}
      {analysis.requiredDocuments.length > 0 && (
        <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">📄 Wymagane dokumenty</h3>
          <ul className="grid md:grid-cols-2 gap-2">
            {analysis.requiredDocuments.map((doc, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-blue-600 mt-0.5">📎</span>
                <span>{doc}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Helper component for points cards
function PointsCard({ icon, title, points, max, description }: any) {
  const percentage = (points / max) * 100;
  const color = percentage >= 75 ? 'green' : percentage >= 50 ? 'yellow' : percentage >= 25 ? 'orange' : 'red';

  return (
    <div className="bg-white rounded-lg p-4 shadow-md border-l-4" style={{ borderColor: `var(--${color}-500)` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-gray-700">
          {icon}
          <span className="font-semibold text-sm">{title}</span>
        </div>
        <span className={`text-2xl font-bold text-${color}-600`}>
          {points}/{max}
        </span>
      </div>
      <div className={`bg-gray-200 rounded-full h-2 overflow-hidden`}>
        <div className={`h-full bg-${color}-500`} style={{ width: `${percentage}%` }} />
      </div>
      <p className="text-xs text-gray-600 mt-2">{description}</p>
    </div>
  );
}

// Helper component for issue cards
function IssueCard({ issue }: { issue: Issue }) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'border-red-500 bg-red-50';
      case 'WARNING': return 'border-yellow-500 bg-yellow-50';
      case 'INFO': return 'border-blue-500 bg-blue-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  return (
    <div className={`border-l-4 rounded p-4 ${getSeverityColor(issue.severity)}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h4 className="font-semibold text-sm mb-1">{issue.category}</h4>
          <p className="text-sm mb-2">{issue.description}</p>
          <div className="bg-white bg-opacity-50 rounded p-2 text-xs">
            <strong>💡 Rekomendacja:</strong> {issue.recommendation}
          </div>
        </div>
      </div>
    </div>
  );
}
